import { useEffect, useRef, useState } from 'react';
import { useMapStore } from '../store/useMapStore';
import { loadLayerState, clearLayerState, loadLayerFilters } from '../store/layerPersistence';
import { loadLayerData } from '../services/layerService';

/**
 * Hook that restores persisted layers on app mount.
 * Reads saved layer configs from localStorage and re-fetches their data.
 * 
 * Call this once in a top-level component (MapPage).
 */
export const useLayerRestore = () => {
  const hasRestored = useRef(false);
  const [isRestoring, setIsRestoring] = useState(false);
  const [restoreError, setRestoreError] = useState<string | null>(null);

  useEffect(() => {
    if (hasRestored.current) return;
    hasRestored.current = true;

    const restoreLayers = async () => {
      const savedState = loadLayerState();
      if (!savedState || savedState.layerIds.length === 0) {
        console.log('[LayerRestore] No persisted layers to restore');
        return;
      }

      // Don't restore if layers are already loaded (e.g., hot reload)
      const currentLayerIds = useMapStore.getState().layerIds;
      if (currentLayerIds.length > 0) {
        console.log('[LayerRestore] Layers already loaded, skipping restore');
        return;
      }

      setIsRestoring(true);
      console.log('[LayerRestore] 🔄 Restoring layers...', {
        count: savedState.layerIds.length,
        names: savedState.layerIds.map(id => savedState.layersConfig[id]?.name),
      });

      const viewport = useMapStore.getState().viewport;

      // Calculate viewport bounds for data loading
      const worldSize = 256 * Math.pow(2, viewport.zoom);
      const lonDegreesPerPixel = 360 / worldSize;
      const latRadians = viewport.latitude * Math.PI / 180;
      const latDegreesPerPixel = (360 / worldSize) / Math.cos(latRadians);
      const lonOffset = (window.innerWidth / 2) * lonDegreesPerPixel * 1.1;
      const latOffset = (window.innerHeight / 2) * latDegreesPerPixel * 1.1;

      const bounds = {
        north: Math.min(viewport.latitude + latOffset, 85),
        south: Math.max(viewport.latitude - latOffset, -85),
        east: viewport.longitude + lonOffset,
        west: viewport.longitude - lonOffset,
      };

      let successCount = 0;
      let errorCount = 0;

      const persistedFilters = loadLayerFilters();

      for (const layerId of savedState.layerIds) {
        const config = savedState.layersConfig[layerId];
        if (!config) {
          console.warn(`[LayerRestore] ⚠️ Config missing for layer ${layerId}, skipping`);
          errorCount++;
          continue;
        }

        try {
          console.log(`[LayerRestore] Loading data for: ${config.name}`);
          const layerFilters = persistedFilters[layerId];
          const { data } = await loadLayerData(config, { ...viewport, bounds }, layerFilters);
          useMapStore.getState().addLayer(config, data);
          successCount++;
        } catch (error) {
          console.error(`[LayerRestore] ❌ Failed to restore layer: ${config.name}`, error);
          errorCount++;
          // Layer might have been deleted server-side — skip it but continue with others
        }
      }

      if (errorCount > 0 && successCount === 0) {
        // All layers failed — likely stale state, clear it
        console.warn('[LayerRestore] All layers failed to restore, clearing saved state');
        clearLayerState();
        setRestoreError('Failed to restore saved layers. They may have been deleted.');
      } else if (errorCount > 0) {
        setRestoreError(`Restored ${successCount} layers, ${errorCount} failed to load.`);
      }

      setIsRestoring(false);
      console.log('[LayerRestore] ✅ Restore complete', { successCount, errorCount });
    };

    restoreLayers();
  }, []);

  return { isRestoring, restoreError };
};
